import juice from 'juice';
import isEmail from 'validator/lib/isEmail';
import styles from '@/components/tiptap/styles/index.css?raw';

// Import all partial CSS files
import codeStyles from '@/components/tiptap/styles/partials/code.css?raw';
import listsStyles from '@/components/tiptap/styles/partials/lists.css?raw';
import placeholderStyles from '@/components/tiptap/styles/partials/placeholder.css?raw';
import tasksStyles from '@/components/tiptap/styles/partials/tasks.css?raw';
import typographyStyles from '@/components/tiptap/styles/partials/typography.css?raw';
import type { SplitEmailContent } from '@/types/email';
import type { MessageDB } from '@/types/message';
import type { CreateAttachmentRequest, EmailName, Message as NylasMessage } from 'nylas';

type CssVariables = Record<string, string>;
// Combine all CSS
const allStyles: string = [
  styles,
  codeStyles,
  listsStyles,
  placeholderStyles,
  tasksStyles,
  typographyStyles,
].join('\n');

/**
 * Extracts CSS variables from root styles
 */
function extractCssVars(css: string): CssVariables {
  const cssVars: CssVariables = {};
  const rootContent = css.match(/:root[ \t\n]*\{([^}]*)\}/)?.[1] || '';
  // eslint-disable-next-line regexp/no-super-linear-backtracking
  const matches = [...rootContent.matchAll(/--([^:]+):\s*([^;]+);/g)];

  for (const match of matches) {
    const [, name, value] = match;
    cssVars[`var(--${name.trim()})`] = value.trim();
  }

  return cssVars;
}

/**
 * Converts an rgb() color to hex format
 * @param rgb - RGB color string (e.g. 'rgb(201,55,44)' or 'rgba(201,55,44,1)')
 * @returns Hex color string (e.g. '#C9372C')
 */
function rgbToHex(rgb: string): string {
  const values = rgb.match(/\d+/g);
  if (!values || values.length < 3)
    return rgb;

  const r = Number.parseInt(values[0], 10);
  const g = Number.parseInt(values[1], 10);
  const b = Number.parseInt(values[2], 10);

  return `#${[r, g, b].map((x: number): string => {
    const hex = x.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  }).join('').toUpperCase()}`;
}

/**
 * Converts style attributes with rgb() colors to hex colors
 * @param styleAttr - Style attribute content
 * @returns Style attribute with rgb() converted to hex
 */
function convertStyleColors(styleAttr: string): string {
  return styleAttr.replaceAll(
    /rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)/g,
    (match: string): string => rgbToHex(match),
  );
}

/**
 * Converts HTML with CSS variables and external styles to email-client compatible HTML
 * @param html - The HTML content to convert
 * @returns Email-client compatible HTML with inline styles
 */
export function convertToEmailHtml(html: string): string {
  // Extract CSS variables
  const cssVars = extractCssVars(allStyles);

  // First inline any regular CSS using juice
  let inlined = juice(html, {
    removeStyleTags: true,
    preserveMediaQueries: false,
    preserveFontFaces: false,
    preserveKeyFrames: false,
    preservePseudos: false,
    applyWidthAttributes: true,
    applyHeightAttributes: true,
    applyAttributesTableElements: true,
  });

  // Replace any remaining CSS variable references with their actual values
  for (const [variable, value] of Object.entries(cssVars)) {
    const safeVariable = variable.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
    // Convert rgb() colors to hex for better email client compatibility
    const hexValue = value.startsWith('rgb') ? rgbToHex(value) : value;
    inlined = inlined.replaceAll(new RegExp(safeVariable, 'g'), hexValue);
  }

  // Convert any rgb colors in style attributes to hex
  inlined = inlined.replaceAll(
    /style="([^"]*)"/g,
    (_match: string, styles: string): string => `style="${convertStyleColors(styles)}"`,
  );

  // Clean up the HTML for email clients
  inlined = inlined
    // Remove all classes as they're not needed in email
    .replaceAll(/\sclass="[^"]*"/g, '')
    // Remove any empty style attributes
    .replaceAll(/\sstyle=""/g, '')
    // Remove any data attributes
    .replaceAll(/\sdata-[^=]*="[^"]*"/g, '');

  return inlined.trim();
}

/**
 * Converts an array of strings to EmailName objects
 * @param emails - Array of email addresses
 * @returns Array of EmailName objects
 */
export function stringArrayToEmailName(emails: string[]): EmailName[] {
  const emailNames = emails.map((email: string) => {
    // Check if the string is a valid email
    return isEmail(email) ? { email } : undefined;
  })
    // Filter out any undefined values
    .filter((emailName): emailName is EmailName => !!emailName);

  return emailNames;
}

/**
 * Formats an array of EmailName objects into a readable string for email headers
 * @param emailNames - Array of EmailName objects
 * @param boldNames - Whether to wrap names in <strong> tags (default: true)
 * @returns Formatted string (e.g., "<strong>John Doe</strong> <john@example.com>, <strong>Jane Smith</strong> <jane@example.com>")
 */
export function formatEmailNameForHeader(emailNames?: EmailName[], boldNames = true): string {
  if (!emailNames || emailNames.length === 0)
    return '';

  return emailNames
    .map((emailName) => {
      if (emailName.name) {
        const name = boldNames ? `<strong>${emailName.name}</strong>` : emailName.name;
        return `${name} &lt;${emailName.email}&gt;`;
      }
      return `&lt;${emailName.email}&gt;`;
    })
    .join(', ');
}

/**
 * Strips trailing <br> and <br/> tags from content
 * @param content - The content to clean
 * @returns Content with trailing line breaks removed
 */
function stripTrailingLineBreaks(content: string): string {
  // Remove trailing <br>, <br/>, <br /> tags (case-insensitive)
  return content.replaceAll(/<br\s*\/?>\s*$/gi, '').trim();
}

/**
 * Extracts user-typed content from above a forwarded message header
 * Returns content before "---------- Forwarded message ---------" marker
 * @param content - The editor content that may contain a forwarded message
 * @returns Content with forwarded message removed, preserving user's typed text
 */
export function extractUserContentFromForward(content: string): string {
  if (!content)
    return '';

  const forwardMarker = '---------- Forwarded message ---------';

  if (content.includes(forwardMarker)) {
    // Find the marker position
    const markerIndex = content.indexOf(forwardMarker);

    // Extract content up to the marker
    let userContent = content.slice(0, markerIndex);

    // Strip trailing <br> tags that were used as separator
    userContent = stripTrailingLineBreaks(userContent);

    // Treat purely empty paragraphs / breaks as no user content
    const textOnly = userContent
      .replaceAll(/<br\s*\/?>(?:\s|&nbsp;)*/gi, '')
      .replaceAll(/<\/?p[^>]*>/gi, '')
      .replaceAll(/&nbsp;/gi, ' ')
      .trim();

    if (!textOnly) {
      return '';
    }

    return userContent;
  }

  return content;
}

/**
 * Creates a forwarded email header with From, Date, Subject, To, and Cc information
 * @param message - The message being forwarded
 * @returns HTML string containing the formatted forwarded message header
 */
export function createForwardedEmailHeader(
  message: MessageDB | Partial<NylasMessage>,
): string {
  const isMessageDB = (msg: MessageDB | Partial<NylasMessage>): msg is MessageDB => {
    return 'created_at' in msg;
  };

  // Extract message properties
  const from = message.from;
  const to = message.to;
  const cc = message.cc;
  const subject = isMessageDB(message)
    ? message.content?.subject
    : message.subject;

  // Get the date - handle both MessageDB and NylasMessage formats
  let date: Date;
  if (isMessageDB(message)) {
    date = new Date(message.created_at);
  } else if (message.date) {
    // NylasMessage date is a Unix timestamp
    date = new Date(message.date * 1000);
  } else {
    date = new Date();
  }

  const formattedDate = date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Build the header HTML
  const headerParts: string[] = [
    '---------- Forwarded message ---------<br>',
  ];

  if (from && from.length > 0) {
    headerParts.push(`From: ${formatEmailNameForHeader(from)}<br>`);
  }

  headerParts.push(`Date: ${formattedDate}<br>`);

  if (subject) {
    headerParts.push(`Subject: ${subject}<br>`);
  }

  if (to && to.length > 0) {
    headerParts.push(`To: ${formatEmailNameForHeader(to)}<br>`);
  }

  if (cc && cc.length > 0) {
    headerParts.push(`Cc: ${formatEmailNameForHeader(cc)}<br>`);
  }

  headerParts.push('<br>');

  return headerParts.join('\n');
}

/**
 * Creates reply context with "On [date], [sender] wrote:" header and quoted original message
 * @param message - The message being replied to
 * @returns HTML string containing the formatted reply context
 */
export function createReplyEmailContext(
  message: MessageDB | Partial<NylasMessage>,
): string {
  const isMessageDB = (msg: MessageDB | Partial<NylasMessage>): msg is MessageDB => {
    return 'created_at' in msg;
  };

  // Extract message properties
  const from = message.from;

  // Get the date - handle both MessageDB and NylasMessage formats
  let date: Date;
  if (isMessageDB(message)) {
    date = new Date(message.created_at);
  } else if (message.date) {
    // NylasMessage date is a Unix timestamp
    date = new Date(message.date * 1000);
  } else {
    date = new Date();
  }

  const formattedDate = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Get the sender name/email for the "On [date], [sender] wrote:" header
  let senderDisplay = '';
  if (from && from.length > 0) {
    const primarySender = from[0];
    senderDisplay = primarySender.name
      ? `${primarySender.name} &lt;${primarySender.email}&gt;`
      : primarySender.email;
  }

  // Get original message body
  const originalBody = isMessageDB(message)
    ? (message.content?.body || '')
    : (message.body || '');

  // Build the reply context
  const contextParts: string[] = [
    `<p class="text-node">On ${formattedDate}, ${senderDisplay} wrote:</p>`,
    `<blockquote>`,
    originalBody,
    '</blockquote>',
  ];

  return contextParts.join('\n');
}

/**
 * Splits HTML email content into main content and quoted content (blockquotes).
 * Useful for showing/hiding quoted reply content in email threads (similar to Gmail's
 * "Show trimmed content" feature).
 * @param html - The HTML content to split
 * @returns Object containing main content, quoted content, and whether quotes exist
 */
export function splitEmailContentByBlockquote(html: string): SplitEmailContent {
  if (!html) {
    return { mainContent: '', quotedContent: '', hasQuotedContent: false };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  const blockquotes = body.querySelectorAll('blockquote');

  if (blockquotes.length === 0) {
    return { mainContent: html, quotedContent: '', hasQuotedContent: false };
  }

  // Collect quoted content and remove blockquotes in a single pass
  const quotedContentParts: string[] = [];
  for (const bq of blockquotes) {
    quotedContentParts.push(bq.outerHTML);
    bq.remove();
  }

  return {
    mainContent: body.innerHTML,
    quotedContent: quotedContentParts.join(''),
    hasQuotedContent: true,
  };
}

/**
 * Extracts user-typed content from above a reply context
 * Returns content before the "On [date], [sender] wrote:" section
 * @param content - The editor content that may contain a reply context
 * @returns Content with reply context removed, preserving user's typed text
 */
export function extractUserContentFromReply(content: string): string {
  if (!content)
    return '';

  // Prefer using the actual HTML structure when running in a browser environment.
  // We look for a <p> whose textContent matches "On ... wrote:" and take everything
  // *before* that paragraph as user-typed content.
  try {
    const container = document.createElement('div');
    container.innerHTML = content;

    const paragraphs = [...container.querySelectorAll('p')];
    const headerParagraph = paragraphs.find((p) => {
      const text = p.textContent?.trim() || '';
      return text.startsWith('On ') && text.endsWith(' wrote:');
    });

    // If we can't find a clear reply header, leave the content unchanged
    if (!headerParagraph) {
      return content;
    }

    const headerHtml = headerParagraph.outerHTML;
    const markerIndex = content.indexOf(headerHtml);

    // If for some reason we can't map the DOM node back into the original HTML,
    // fall back to preserving the original content
    if (markerIndex === -1) {
      return content;
    }

    // Extract content up to the header paragraph
    let userContent = content.slice(0, markerIndex);

    // Strip trailing <br> tags that were used as separators
    userContent = stripTrailingLineBreaks(userContent);

    // Treat purely empty paragraphs / breaks as no user content
    const temp = document.createElement('div');
    temp.innerHTML = userContent;

    const text = temp.textContent?.replaceAll('\u00A0', ' ').trim() ?? '';
    const isEffectivelyEmpty = text.length === 0;

    if (isEffectivelyEmpty) {
      // No actual user content above the reply header
      return '';
    }

    return userContent;
  } catch {
    // In non-DOM environments or on parsing errors, fall back to the legacy
    // marker-based behavior to avoid breaking server-side usage.
    const replyMarker = '<br>On ';

    if (content.includes(replyMarker)) {
      const markerIndex = content.indexOf(replyMarker);
      let userContent = content.slice(0, markerIndex);
      userContent = stripTrailingLineBreaks(userContent);
      return userContent;
    }

    return content;
  }
}

/**
 * Converts a File object to Nylas CreateAttachmentRequest format.
 * Reads the file as base64 for the content field.
 */
export async function fileToAttachment(file: File): Promise<CreateAttachmentRequest> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCodePoint(byte);
  }
  const base64Content = btoa(binary);

  return {
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    content: base64Content,
    size: file.size,
  };
}
